import { Observer } from './Observer';
import { generateTeaser } from './Utils';
import { FilterState } from './FilterState';
import { FilterApi } from './FilterApi';
export class Filter extends Observer {
    get mainContent() {
        return document.querySelector('.js-main');
    }
    get categoryContent() {
        return document.querySelector('.js-category-content');
    }
    get filterOverview() {
        return document.querySelector('.js-filter-overview');
    }
    constructor() {
        var _a, _b;
        super();
        this.html = document.documentElement;
        this.filter = document.querySelector('.js-filter');
        this.toggleButton = document.querySelector('.js-toggle-filter');
        this.submitButton = document.querySelector('.js-submit-filter');
        this.optionsPanel = document.querySelector('.js-filter-options');
        this.teaserTemplate = document.querySelector('#teaser-template');
        this.state = new FilterState();
        this.url = encodeURI((_a = this.filter.getAttribute('data-url')) !== null && _a !== void 0 ? _a : '');
        this.api = new FilterApi(this.url);
        this.isOpen = false;
        this.isLoading = false;
        this.filteredArticles = [];
        this.hasResults = false;
        this.onToggleFilter = () => this.toggleFilter();
        this.onSubmitFilter = () => this.applyFilter();
        this.onToggleFilterParameter = (e) => this.toggleFilterParameter(e);
        this.onKeyDown = (e) => {
            if (!this.isOpen)
                return;
            const panel = this.optionsPanel;
            if (e.key === 'Escape' || e.key === 'Esc') {
                e.preventDefault();
                this.closeFilter();
                return;
            }
            if (e.key === 'Tab') {
                const focusable = this.getFocusableElements(panel);
                if (!focusable.length)
                    return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                else {
                    if (document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        this.onDocumentClick = (e) => {
            if (!this.isOpen)
                return;
            const target = e.target;
            if (this.filter.contains(target) || this.toggleButton.contains(target)) {
                return;
            }
            this.closeFilter();
        };
        this.originalMainContent = this.mainContent.innerHTML;
        this.defaultSubmitButtonText = (_b = this.submitButton.textContent) !== null && _b !== void 0 ? _b : '';
        this.initListeners();
        this.initFixedFilters();
        this.updateConditionalVisibility();
    }
    initListeners() {
        this.toggleButton.addEventListener('click', this.onToggleFilter);
        this.submitButton.addEventListener('click', this.onSubmitFilter);
        document.querySelectorAll('.js-select-filter-parameter').forEach(btn => {
            btn.addEventListener('click', this.onToggleFilterParameter);
        });
    }
    initFixedFilters() {
        document.querySelectorAll('.js-select-filter-parameter[data-filter-fixed="true"]').forEach(btn => {
            var _a;
            const id = btn.dataset.filterValue;
            const type = btn.dataset.filterType;
            const label = (_a = btn.textContent) === null || _a === void 0 ? void 0 : _a.trim();
            const parentId = btn.dataset.filterParent;
            if (!id || !type || !label)
                return;
            this.state.toggle(type, Object.assign({ id,
                label, fixed: true }, (parentId ? { parentId } : {})));
        });
    }
    toggleFilter() {
        this.notifyObservers();
        this.isOpen ? this.closeFilter() : this.openFilter();
    }
    openFilter() {
        this.isOpen = true;
        this.html.classList.add('dialog-open');
        this.filter.classList.add('filter--open');
        this.adjustOptionsPanelHeight();
        document.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onDocumentClick);
    }
    closeFilter() {
        if (!this.isOpen)
            return;
        this.isOpen = false;
        this.animateOptionsPanel(0);
        this.html.classList.remove('dialog-open');
        this.filter.classList.remove('filter--open');
        this.toggleButton.focus();
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onDocumentClick);
    }
    getFocusableElements(container) {
        return Array.from(container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    }
    toggleFilterParameter(e) {
        e.preventDefault();
        const link = e.currentTarget;
        if (link.dataset.filterFixed === 'true')
            return;
        const id = link.dataset.filterValue;
        const filterType = link.dataset.filterType;
        const label = link.textContent.trim();
        const parentId = link.dataset.filterParent;
        if (!id || !filterType || !label)
            return;
        this.state.toggle(filterType, Object.assign({ id,
            label }, (parentId ? { parentId } : {})));
        this.updateParameterButtons();
        this.updateConditionalVisibility();
        this.debouncePreviewResults();
    }
    async removeFilterParameter(e) {
        const button = e.currentTarget;
        const id = button.dataset.id;
        const filterType = button.dataset.filterType;
        if (!id || !filterType)
            return;
        this.state.remove(filterType, id);
        this.updateParameterButtons();
        this.updateConditionalVisibility();
        if (this.state.hasActive) {
            await this.fetchFilteredArticles();
            this.renderAppliedResults();
        }
        else {
            this.resetFilter();
        }
    }
    updateParameterButtons() {
        document.querySelectorAll('.js-select-filter-parameter').forEach(el => {
            const id = el.dataset.filterValue;
            const type = el.dataset.filterType;
            if (!id || !type)
                return;
            const isActive = this.state.all[type].some(f => f.id === id);
            el.classList.toggle('filter-parameter--active', isActive);
        });
    }
    updateConditionalVisibility() {
        const sections = document.querySelectorAll('[data-show-on]');
        sections.forEach(section => this.updateSectionVisibility(section));
        if (this.isOpen) {
            this.adjustOptionsPanelHeight();
        }
    }
    adjustOptionsPanelHeight() {
        const oldHeight = this.optionsPanel.offsetHeight;
        if (!this.isOpen)
            return;
        this.optionsPanel.style.height = 'auto';
        const targetHeight = this.optionsPanel.scrollHeight;
        // Hack for smooth transition
        this.optionsPanel.style.height = oldHeight + 'px';
        this.animateOptionsPanel(targetHeight);
    }
    animateOptionsPanel(targetHeight) {
        const startHeight = this.optionsPanel.offsetHeight;
        if (startHeight === targetHeight)
            return;
        this.optionsPanel.style.height = startHeight + 'px';
        requestAnimationFrame(() => {
            this.optionsPanel.style.height = targetHeight + 'px';
        });
    }
    updateSectionVisibility(section) {
        const triggerSelector = section.dataset.showOn;
        if (!triggerSelector)
            return;
        const trigger = document.querySelector(triggerSelector);
        section.style.display = (trigger === null || trigger === void 0 ? void 0 : trigger.classList.contains('filter-parameter--active')) ? 'block' : 'none';
    }
    applyFilter() {
        if (this.state.hasActive) {
            this.renderAppliedResults();
        }
        else {
            this.resetFilter();
        }
        this.closeFilter();
    }
    resetFilter() {
        this.state.reset();
        this.clearResults();
        this.updateParameterButtons();
        this.isLoading = false;
        this.renderSubmitButton();
        this.mainContent.innerHTML = this.originalMainContent;
        this.updateConditionalVisibility();
    }
    async fetchFilteredArticles() {
        if (!this.state.hasActive) {
            this.clearResults();
            return;
        }
        try {
            this.filteredArticles = await this.api.fetchResults(this.buildFilterQueryParams());
            this.hasResults = this.filteredArticles.length > 0;
        }
        catch (e) {
            console.error(e);
            this.clearResults();
        }
    }
    clearResults() {
        this.filteredArticles = [];
        this.hasResults = false;
    }
    buildFilterQueryParams() {
        var _a;
        const params = new URLSearchParams();
        const { category, country, province } = this.state.all;
        this.addParam(params, 'categories', category);
        this.addParam(params, 'country', country);
        if (province.length) {
            const groupedByMeta = {};
            province.forEach(filter => {
                if (!filter.parentId)
                    return;
                if (!groupedByMeta[filter.parentId]) {
                    groupedByMeta[filter.parentId] = [];
                }
                groupedByMeta[filter.parentId].push(filter.id);
            });
            Object.entries(groupedByMeta).forEach(([parentId, values]) => {
                params.set(parentId, values.join('|'));
            });
        }
        params.set('lang', (_a = this.filter.getAttribute('data-lang')) !== null && _a !== void 0 ? _a : '');
        return params;
    }
    addParam(params, key, values) {
        if (!values.length)
            return;
        params.set(key, values.map(v => v.id).join('|'));
    }
    debouncePreviewResults() {
        this.setLoading(true);
        window.clearTimeout(this.debounceTimer);
        this.debounceTimer = window.setTimeout(async () => {
            await this.fetchFilteredArticles();
            this.setLoading(false);
        }, 400);
    }
    setLoading(state) {
        this.isLoading = state;
        this.renderSubmitButton();
    }
    renderAppliedResults() {
        this.renderSubmitButton();
        this.renderFilterOverview();
        this.renderFilteredArticles();
    }
    renderSubmitButton() {
        const button = this.submitButton;
        const disabled = this.isLoading || (this.state.hasActive && !this.hasResults);
        button.disabled = disabled;
        button.textContent = this.getSubmitButtonText(button);
        button.classList.toggle('button--primary-disabled', disabled);
    }
    getSubmitButtonText(button) {
        var _a, _b, _c, _d;
        if (this.isLoading)
            return (_a = button.getAttribute('data-loading')) !== null && _a !== void 0 ? _a : 'Loading...';
        if (!this.state.hasActive)
            return this.defaultSubmitButtonText;
        if (this.state.hasActive && !this.hasResults)
            return `0 ${(_b = this.filter.getAttribute('data-result-plural')) !== null && _b !== void 0 ? _b : 'results'}`;
        const count = this.filteredArticles.length;
        return count === 1
            ? `${count} ${(_c = button.getAttribute('data-result-singular')) !== null && _c !== void 0 ? _c : 'result'}`
            : `${count} ${(_d = button.getAttribute('data-result-plural')) !== null && _d !== void 0 ? _d : 'results'}`;
    }
    renderFilterOverview() {
        if (!this.state.hasActive)
            return;
        let overview = this.filterOverview;
        if (!overview) {
            overview = this.createFilterOverview();
            this.categoryContent.parentNode.insertBefore(overview, this.categoryContent);
        }
        else {
            overview.replaceWith(this.createFilterOverview());
        }
    }
    createFilterOverview() {
        var _a;
        const div = document.createElement('div');
        div.classList.add('filter-overview', 'js-filter-overview');
        div.innerHTML = `
            <ul class="filter-parameter-list filter-parameter-list--overview">
                ${this.getActiveFilterItemsHtml()}
            </ul>
            <button class="filter-reset js-reset-filter">
                <i class="obb-icon-repeat" aria-hidden="true"></i>
                ${this.filter.getAttribute('data-reset')}
            </button>
        `;
        div.querySelectorAll('.js-remove-filter-parameter').forEach(btn => btn.addEventListener('click', e => this.removeFilterParameter(e)));
        (_a = div.querySelector('.js-reset-filter')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => this.resetFilter());
        return div;
    }
    getActiveFilterItemsHtml() {
        return Object.keys(this.state.all)
            .flatMap(type => this.state.all[type]
            .filter(f => !f.fixed)
            .map(f => `
                            <li class="filter-parameter-list__item">
                                <span class="filter-parameter filter-parameter--active filter-parameter--removeable">
                                    ${f.label}
                                    <button class="filter-parameter__remove js-remove-filter-parameter" data-id="${f.id}" data-filter-type="${type}" aria-label="remove filter">&times;</button>
                                </span>
                            </li>
                        `))
            .join('');
    }
    renderFilteredArticles() {
        const articlesHtml = `
            ${this.renderHeading()}
            ${this.renderTeasers()}
        `;
        this.categoryContent.innerHTML = articlesHtml;
        // fix IE issue
        this.teaserTemplate = document.querySelector('#teaser-template');
        this.renderCredits();
    }
    renderHeading() {
        const count = this.filteredArticles.length;
        const heading = count === 1 ? `${count} ${this.filter.getAttribute('data-result-singular')}` : `${count} ${this.filter.getAttribute('data-result-plural')}`;
        return `<h1>${heading}</h1>`;
    }
    renderTeasers() {
        return `
            <div class="row">
                ${this.filteredArticles.map(teaser => this.getTeaserHtml(teaser)).join('')}
            </div>
        `;
    }
    getTeaserHtml(teaser) {
        const image = this.getTeaserImage(teaser);
        return generateTeaser(this.teaserTemplate, {
            href: teaser['@path'],
            imageSrc: image.src,
            imageSrcSet: image.srcSet,
            imagePreview: image.preview,
            imageAlt: image.alt,
            imageTitle: image.title,
            category: this.getCategoryDisplayName(teaser.categories),
            title: teaser.title,
            leadin: teaser.leadin,
        });
    }
    getCategoryDisplayName(categories, excludeNames = ['reiseblog']) {
        var _a, _b, _c, _d;
        if (!Array.isArray(categories) || !categories.length) {
            return '';
        }
        if (categories.length === 1)
            return (_a = categories[0].displayName) !== null && _a !== void 0 ? _a : '';
        const filtered = categories.filter(cat => { var _a; return !excludeNames.includes((_a = cat['@name']) !== null && _a !== void 0 ? _a : ''); });
        return (_d = (_c = (_b = filtered[0]) === null || _b === void 0 ? void 0 : _b.displayName) !== null && _c !== void 0 ? _c : categories[0].displayName) !== null && _d !== void 0 ? _d : '';
    }
    getTeaserImage(teaser) {
        var _a, _b, _c;
        const image = this.resolveTeaserImage(teaser);
        return {
            src: (_a = image === null || image === void 0 ? void 0 : image['@link']) !== null && _a !== void 0 ? _a : '',
            srcSet: this.buildImageSrcSet(image),
            preview: (image === null || image === void 0 ? void 0 : image['@link']) ? `${image['@link']}?rendition=25` : '#',
            alt: (_c = (_b = image === null || image === void 0 ? void 0 : image.metadata) === null || _b === void 0 ? void 0 : _b.description) !== null && _c !== void 0 ? _c : '',
            title: this.getImageTitle(image),
        };
    }
    resolveTeaserImage(teaser) {
        var _a;
        return ((_a = teaser === null || teaser === void 0 ? void 0 : teaser.teaserImg) === null || _a === void 0 ? void 0 : _a['@link']) ? teaser.teaserImg : null;
    }
    buildImageSrcSet(image, normal = '0.33', retina = '0.66') {
        if (!(image === null || image === void 0 ? void 0 : image['@link']))
            return '';
        return `${image['@link']}?rendition=${normal}, ${image['@link']}?rendition=${retina} 2x`;
    }
    getImageTitle(image) {
        var _a, _b, _c, _d;
        const title = (_b = (_a = image === null || image === void 0 ? void 0 : image.metadata) === null || _a === void 0 ? void 0 : _a.title) === null || _b === void 0 ? void 0 : _b.trim();
        const rights = (_d = (_c = image === null || image === void 0 ? void 0 : image.metadata) === null || _c === void 0 ? void 0 : _c.rights) === null || _d === void 0 ? void 0 : _d.trim();
        if (title && rights) {
            return `${title} - © ${rights}`;
        }
        if (rights) {
            return `© ${rights}`;
        }
        if (title) {
            return title;
        }
        return '';
    }
    renderCredits() {
        const list = document.querySelector('.js-image-credits-list');
        if (!list)
            return;
        const credits = this.filteredArticles
            .map((teaser, i, arr) => {
            var _a, _b, _c, _d;
            const image = this.resolveTeaserImage(teaser);
            if (!image)
                return null;
            const title = (_b = (_a = image === null || image === void 0 ? void 0 : image.metadata) === null || _a === void 0 ? void 0 : _a.title) === null || _b === void 0 ? void 0 : _b.trim();
            const rights = (_d = (_c = image === null || image === void 0 ? void 0 : image.metadata) === null || _c === void 0 ? void 0 : _c.rights) === null || _d === void 0 ? void 0 : _d.trim();
            if (!title && !rights)
                return null;
            let credit = title !== null && title !== void 0 ? title : '';
            if (rights) {
                const formattedRights = rights.includes('©') ? rights.replace('©', '-') : rights + (i !== arr.length - 1 ? ';' : '');
                credit += title ? ` - ${formattedRights}` : formattedRights;
            }
            return credit;
        })
            .filter(Boolean);
        list.innerHTML = credits.map(c => `<li>${c.trim()}</li>`).join('');
    }
    notify() {
        this.closeFilter();
    }
}
export function initFilter(menu, searchToggler) {
    if (document.querySelector('.js-filter')) {
        const filter = new Filter();
        menu.addObserver(filter);
        if (searchToggler)
            searchToggler.addObserver(filter);
        filter.addObserver(menu);
        if (searchToggler)
            filter.addObserver(searchToggler);
    }
}
